import React, { useState, useEffect } from 'react'
import { BarChart, LineChart, PieChart, TrendingUp, Users, Award, Activity, Calendar, Download, Filter } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns'

interface PerformanceData {
  overview: {
    total_chores_completed: number
    total_points_earned: number
    average_completion_time: string
    on_time_rate: number
    participation_rate: number
  }
  by_user: Array<{
    user_id: string
    username: string
    display_name: string
    chores_completed: number
    points_earned: number
    average_points: number
    completion_rate: number
    streak: number
  }>
  by_chore: Array<{
    chore_id: string
    chore_name: string
    times_completed: number
    average_completion_time: string
    completion_rate: number
    most_completed_by: string
  }>
  trends: {
    daily_completions: Array<{ date: string; count: number }>
    weekly_points: Array<{ week: string; points: number }>
    completion_rates: Array<{ period: string; rate: number }>
  }
  achievements: Array<{
    user: string
    achievement: string
    date: string
  }>
}

export const PerformanceReports: React.FC = () => {
  const [data, setData] = useState<PerformanceData | null>(null)
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month')
  const [selectedUser, setSelectedUser] = useState<string>('all')
  const [reportType, setReportType] = useState<'overview' | 'users' | 'chores' | 'trends'>('overview')

  useEffect(() => {
    loadReports()
  }, [dateRange, selectedUser])

  const loadReports = async () => {
    setLoading(true)
    try {
      const endDate = new Date()
      let startDate = new Date()

      switch (dateRange) {
        case 'week':
          startDate = subDays(endDate, 7)
          break
        case 'month':
          startDate = subDays(endDate, 30)
          break
        case 'quarter':
          startDate = subDays(endDate, 90)
          break
        case 'year':
          startDate = subDays(endDate, 365)
          break
      }

      const params = new URLSearchParams({
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd')
      })

      if (selectedUser !== 'all') {
        params.append('user_id', selectedUser)
      }

      const response = await apiClient.get(`/api/reports/performance?${params}`)

      // Transform the data to match our interface
      const performanceData: PerformanceData = {
        overview: {
          total_chores_completed: response.total_completed || 0,
          total_points_earned: response.total_points || 0,
          average_completion_time: response.avg_completion_time || '0h',
          on_time_rate: response.on_time_rate || 0,
          participation_rate: response.participation_rate || 0
        },
        by_user: response.user_stats || [],
        by_chore: response.chore_stats || [],
        trends: {
          daily_completions: response.daily_stats || [],
          weekly_points: response.weekly_points || [],
          completion_rates: response.completion_trends || []
        },
        achievements: response.achievements || []
      }

      setData(performanceData)
    } catch (error) {
      console.error('Failed to load performance reports:', error)
      // Set default data structure
      setData({
        overview: {
          total_chores_completed: 0,
          total_points_earned: 0,
          average_completion_time: '0h',
          on_time_rate: 0,
          participation_rate: 0
        },
        by_user: [],
        by_chore: [],
        trends: {
          daily_completions: [],
          weekly_points: [],
          completion_rates: []
        },
        achievements: []
      })
    } finally {
      setLoading(false)
    }
  }

  const handleExport = () => {
    if (!data) return

    let csvData: any[][] = []

    switch (reportType) {
      case 'overview':
        csvData = [
          ['Performance Report Overview', format(new Date(), 'yyyy-MM-dd')],
          [],
          ['Metric', 'Value'],
          ['Total Chores Completed', data.overview.total_chores_completed],
          ['Total Points Earned', data.overview.total_points_earned],
          ['Average Completion Time', data.overview.average_completion_time],
          ['On-Time Rate', `${data.overview.on_time_rate}%`],
          ['Participation Rate', `${data.overview.participation_rate}%`]
        ]
        break

      case 'users':
        csvData = [
          ['User Performance Report', format(new Date(), 'yyyy-MM-dd')],
          [],
          ['Username', 'Display Name', 'Chores Completed', 'Points Earned', 'Completion Rate', 'Streak'],
          ...data.by_user.map(user => [
            user.username,
            user.display_name,
            user.chores_completed,
            user.points_earned,
            `${user.completion_rate}%`,
            user.streak
          ])
        ]
        break

      case 'chores':
        csvData = [
          ['Chore Performance Report', format(new Date(), 'yyyy-MM-dd')],
          [],
          ['Chore Name', 'Times Completed', 'Avg Completion Time', 'Completion Rate', 'Most Completed By'],
          ...data.by_chore.map(chore => [
            chore.chore_name,
            chore.times_completed,
            chore.average_completion_time,
            `${chore.completion_rate}%`,
            chore.most_completed_by
          ])
        ]
        break

      case 'trends':
        csvData = [
          ['Performance Trends Report', format(new Date(), 'yyyy-MM-dd')],
          [],
          ['Date', 'Completions'],
          ...data.trends.daily_completions.map(day => [
            format(new Date(day.date + 'T12:00:00'), 'yyyy-MM-dd'),
            day.count
          ])
        ]
        break
    }

    const csv = csvData.map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `performance-${reportType}-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('Report exported successfully')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Performance Reports</h2>
          <p className="text-gray-600">Analyze chore completion and user performance</p>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
          <button
            onClick={handleExport}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'users', 'chores', 'trends'].map((type) => (
            <button
              key={type}
              onClick={() => setReportType(type as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                reportType === type
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {type}
            </button>
          ))}
        </nav>
      </div>

      {/* Report Content */}
      {reportType === 'overview' && (
        <div>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Total Completed</p>
                  <p className="text-2xl font-bold">{data?.overview.total_chores_completed || 0}</p>
                </div>
                <Activity className="w-8 h-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Points Earned</p>
                  <p className="text-2xl font-bold">{data?.overview.total_points_earned || 0}</p>
                </div>
                <Award className="w-8 h-8 text-yellow-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Avg Time</p>
                  <p className="text-2xl font-bold">{data?.overview.average_completion_time || '0h'}</p>
                </div>
                <Calendar className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">On-Time Rate</p>
                  <p className="text-2xl font-bold">{data?.overview.on_time_rate || 0}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Participation</p>
                  <p className="text-2xl font-bold">{data?.overview.participation_rate || 0}%</p>
                </div>
                <Users className="w-8 h-8 text-indigo-500" />
              </div>
            </div>
          </div>

          {/* Recent Achievements */}
          {data?.achievements && data.achievements.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Recent Achievements</h3>
              <div className="space-y-3">
                {data.achievements.slice(0, 5).map((achievement, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <div className="flex items-center">
                      <Award className="w-5 h-5 text-yellow-600 mr-3" />
                      <div>
                        <p className="font-medium">{achievement.user}</p>
                        <p className="text-sm text-gray-600">{achievement.achievement}</p>
                      </div>
                    </div>
                    <span className="text-sm text-gray-500">
                      {format(new Date(achievement.date + 'T12:00:00'), 'MMM d')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {reportType === 'users' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">User Performance Rankings</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Chores Completed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Points Earned
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Points
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completion Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Streak
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.by_user.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.display_name}</div>
                        <div className="text-sm text-gray-500">@{user.username}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.chores_completed}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.points_earned}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.average_points}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${user.completion_rate}%` }}
                          />
                        </div>
                        <span className="text-sm">{user.completion_rate}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.streak > 7
                          ? 'bg-green-100 text-green-800'
                          : user.streak > 3
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.streak} days
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reportType === 'chores' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">Chore Performance Analysis</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Chore Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Times Completed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Completion Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completion Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Most Completed By
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.by_chore.map((chore) => (
                  <tr key={chore.chore_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {chore.chore_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {chore.times_completed}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {chore.average_completion_time}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${
                              chore.completion_rate >= 80
                                ? 'bg-green-500'
                                : chore.completion_rate >= 60
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                            }`}
                            style={{ width: `${chore.completion_rate}%` }}
                          />
                        </div>
                        <span className="text-sm">{chore.completion_rate}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {chore.most_completed_by}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reportType === 'trends' && (
        <div className="space-y-6">
          {/* Daily Completions Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Daily Completion Trend</h3>
            {data?.trends.daily_completions && data.trends.daily_completions.length > 0 ? (
              <div className="space-y-2">
                {data.trends.daily_completions.slice(-14).map((day, idx) => (
                  <div key={idx} className="flex items-center">
                    <span className="text-sm text-gray-600 w-20">
                      {format(new Date(day.date + 'T12:00:00'), 'MMM d')}
                    </span>
                    <div className="flex-1 mx-4">
                      <div className="bg-gray-200 rounded-full h-6">
                        <div
                          className="bg-blue-500 h-6 rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${Math.min((day.count / 30) * 100, 100)}%` }}
                        >
                          <span className="text-xs text-white font-semibold">{day.count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No trend data available</p>
            )}
          </div>

          {/* Weekly Points Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Weekly Points Distribution</h3>
            {data?.trends.weekly_points && data.trends.weekly_points.length > 0 ? (
              <div className="space-y-2">
                {data.trends.weekly_points.map((week, idx) => (
                  <div key={idx} className="flex items-center">
                    <span className="text-sm text-gray-600 w-24">{week.week}</span>
                    <div className="flex-1 mx-4">
                      <div className="bg-gray-200 rounded-full h-6">
                        <div
                          className="bg-yellow-500 h-6 rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${Math.min((week.points / 1000) * 100, 100)}%` }}
                        >
                          <span className="text-xs text-white font-semibold">{week.points}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No weekly data available</p>
            )}
          </div>

          {/* Completion Rate Trend */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Completion Rate Trend</h3>
            {data?.trends.completion_rates && data.trends.completion_rates.length > 0 ? (
              <div className="space-y-2">
                {data.trends.completion_rates.map((period, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="text-sm font-medium">{period.period}</span>
                    <div className="flex items-center">
                      <div className="w-32 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className={`h-2 rounded-full ${
                            period.rate >= 80
                              ? 'bg-green-500'
                              : period.rate >= 60
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${period.rate}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold">{period.rate}%</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No rate data available</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}